import { Alert, Button, Chip, LinearProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useDisputes, useRaiseDispute, useVoteOnDispute } from '../hooks/useMarkets';
import { getErrorMessage } from '../services/api-client';
import { useAuthStore } from '../stores/authStore';
import { colors, mono } from '../themes/theme';

interface DisputePanelProps {
    marketId: number;
    /** Only someone who staked may raise or vote on a dispute. */
    canParticipate: boolean;
    settled: boolean;
}

export function DisputePanel({ marketId, canParticipate, settled }: DisputePanelProps) {
    const session = useAuthStore((s) => s.session);
    const disputes = useDisputes(marketId);
    const raise = useRaiseDispute();
    const vote = useVoteOnDispute();
    const [reason, setReason] = useState('');

    const open = disputes.data?.find((dispute) => dispute.closedAt === null);
    const closed = disputes.data?.filter((dispute) => dispute.closedAt !== null) ?? [];

    if (open) {
        const myVote = open.votes.find((v) => v.userId === session?.id);
        const total = open.agree + open.disagree;

        return (
            <Paper sx={{ p: 1.75, borderLeft: `3px solid ${colors.loss}` }}>
                <Typography variant="overline" sx={{ color: colors.loss }}>
                    Disputed by {open.raisedByHandle}
                </Typography>
                <Typography sx={{ mt: 0.5 }}>{open.reason}</Typography>

                <Stack direction="row" spacing={2} sx={{ mt: 1.5, mb: 1 }}>
                    <Typography sx={{ fontFamily: mono, fontSize: '0.8rem', color: colors.loss }}>
                        {open.agree} say it&rsquo;s wrong
                    </Typography>
                    <Typography sx={{ fontFamily: mono, fontSize: '0.8rem', color: colors.win }}>
                        {open.disagree} say it stands
                    </Typography>
                </Stack>
                <LinearProgress
                    variant="determinate"
                    value={total === 0 ? 0 : (open.agree / total) * 100}
                    sx={{
                        height: 5,
                        bgcolor: colors.win,
                        '& .MuiLinearProgress-bar': { bgcolor: colors.loss },
                    }}
                />

                {canParticipate ? (
                    myVote ? (
                        <Alert severity="info" sx={{ mt: 1.5 }}>
                            You said it {myVote.agrees ? 'was wrong' : 'stands'}. Waiting on the
                            others.
                        </Alert>
                    ) : (
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                            <Button
                                variant="contained"
                                color="error"
                                disabled={vote.isPending}
                                onClick={() =>
                                    vote.mutate({ disputeId: open.id, agrees: true, marketId })
                                }
                            >
                                It&rsquo;s wrong
                            </Button>
                            <Button
                                variant="outlined"
                                color="inherit"
                                disabled={vote.isPending}
                                onClick={() =>
                                    vote.mutate({ disputeId: open.id, agrees: false, marketId })
                                }
                            >
                                It stands
                            </Button>
                        </Stack>
                    )
                ) : (
                    <Typography variant="body2" sx={{ color: colors.muted, mt: 1.5 }}>
                        Only people who bet on this get a vote.
                    </Typography>
                )}

                {vote.isError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {getErrorMessage(vote.error)}
                    </Alert>
                )}
                <Typography variant="body2" sx={{ color: colors.muted, mt: 1.5 }}>
                    A majority of everyone who bet has to agree. If they do, the bet is voided and
                    every stake goes back.
                </Typography>
            </Paper>
        );
    }

    return (
        <Stack spacing={1}>
            {closed.map((dispute) => (
                <Paper key={dispute.id} sx={{ p: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" gap={1}>
                        <Typography variant="overline" sx={{ color: colors.muted }}>
                            Disputed by {dispute.raisedByHandle}
                        </Typography>
                        <Chip
                            size="small"
                            label={dispute.outcome === 'voided' ? 'Overturned' : 'Call stood'}
                            sx={{
                                bgcolor: colors.raised,
                                color: dispute.outcome === 'voided' ? colors.loss : colors.win,
                            }}
                        />
                    </Stack>
                    <Typography variant="body2" sx={{ color: colors.inkSoft }}>
                        {dispute.reason}
                    </Typography>
                </Paper>
            ))}

            {settled && canParticipate && (
                <Paper sx={{ p: 1.75 }}>
                    <Typography variant="overline" sx={{ color: colors.muted }}>
                        Think that&rsquo;s wrong?
                    </Typography>
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        <TextField
                            label="What's wrong with it"
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            size="small"
                        />
                        {raise.isError && (
                            <Alert severity="error">{getErrorMessage(raise.error)}</Alert>
                        )}
                        <Button
                            color="inherit"
                            variant="outlined"
                            disabled={reason.trim().length < 3 || raise.isPending}
                            onClick={() =>
                                raise.mutate(
                                    { marketId, reason: reason.trim() },
                                    { onSuccess: () => setReason('') },
                                )
                            }
                        >
                            {raise.isPending ? 'Raising…' : 'Dispute it'}
                        </Button>
                    </Stack>
                </Paper>
            )}
        </Stack>
    );
}
